import React from "react";
import {
	matchPath,
	Redirect,
	Route,
	Switch,
	useLocation,
	useRouteMatch,
} from "react-router-dom";

import {
	MainBreadcrumbs,
	PageContainer,
	QueryStateIndicators,
	ScrollToTop,
	ScrollUpButton,
	Tab,
	ErrorImage,
} from "@components";
import { useUserDetails, useUserSummary, usePageTitle } from "@hooks";
import { UserWithBalanceAccounts } from "@models";
import Routes, { UserRouteParams } from "@service/navigation/routes";
import ProductsPage from "../products/ProductsPage";
import VouchersPage from "../vouchers/DefaultFilterableVoucherList";
import EditUserPage from "./components/EditUserPage";
import ContractsPage from "./contracts/ContractsPage";
import UserDetailsPageContext from "./UserDetailsPageContext";
import UserStatisticsPage from "./components/UserStatistics/UserStatisticsPage";
import UserDetailsCard from "./components/UserDetailsCard";
import UserRolesPage from "./components/UserRoles/UserRolesPage";

const USER_TABS = [
	{
		route: Routes.UserBasicData,
		tabTo: Routes.UserDetails,
		title: "Stammdaten",
		exact: true as const,
		renderContent: (user: UserWithBalanceAccounts) => (
			<EditUserPage user={user} />
		),
	},
	{
		route: Routes.UserProducts,
		title: "Module",
		renderContent: (user: UserWithBalanceAccounts) => (
			<ProductsPage user={user} />
		),
	},
	{
		route: Routes.UserVouchers,
		title: "Belege",
		renderContent: (user: UserWithBalanceAccounts) => (
			<VouchersPage requiredFilters={{ userId: [user.userId] }} />
		),
	},
	{
		route: Routes.UserContracts,
		title: "Dauerbelege",
		renderContent: (user: UserWithBalanceAccounts) => (
			<ContractsPage user={user} />
		),
	},
	{
		route: Routes.UserRoles,
		title: "Berechtigungen",
		renderContent: (user: UserWithBalanceAccounts) => (
			<UserRolesPage user={user} />
		),
	},
	{
		route: Routes.Statistics,
		title: "Monatsübersicht",
		renderContent: (user: UserWithBalanceAccounts) => (
			<UserStatisticsPage user={user} />
		),
	},
];

export default function UserDetailsPage() {
	const { params } = useRouteMatch<UserRouteParams>();
	const cachedLocation = useLocation();

	const activeTab = USER_TABS.find(
		({ route }) => !!matchPath(cachedLocation.pathname, { path: route.route })
	);

	const [userQuery, refreshUser] = useUserDetails(params.userId);
	const user = userQuery.data;

	const userSummary = useUserSummary(params.userId);

	usePageTitle(
		user
			? [
					activeTab?.title,
					`${user.firstname} ${user.lastname}`,
					userSummary.data?.division.name,
					userSummary.data?.customer.name,
			  ]
					.filter(Boolean)
					.join(" · ")
			: undefined
	);

	return (
		<PageContainer>
			{userSummary.state === "error" ? (
				<ErrorImage />
			) : (
				<>
					<ScrollToTop />
					<ScrollUpButton />

					{userQuery.data && userSummary.data && (
						<>
							<MainBreadcrumbs
								customer={userSummary.data.customer}
								division={userSummary.data.division}
								user={userQuery.data}
							/>
							<UserDetailsCard
								user={userQuery.data}
								moduleSettings={userSummary.data.moduleSettings}
								customer={userSummary.data.customer}
								division={userSummary.data.division}
							/>
						</>
					)}
					<Tab.Container
						css={`
							margin-bottom: 1rem;
						`}
					>
						{USER_TABS.map((tab) => (
							<Tab.Item
								key={tab.route.route}
								active={activeTab === tab}
								to={(tab.tabTo ?? tab.route).makePath(params)}
							>
								{tab.title}
							</Tab.Item>
						))}
					</Tab.Container>
					<div data-testid="subpage-content">
						{userQuery.state === "success" && !!user && (
							<UserDetailsPageContext.Provider
								value={{
									user,
									division: userSummary.data?.division,
									triggerRefresh: refreshUser,
								}}
							>
								<Switch location={cachedLocation}>
									{USER_TABS.map((tab) => (
										<Route
											key={tab.route.route}
											exact={tab.exact}
											path={tab.route.route}
										>
											{tab.renderContent(user)}
										</Route>
									))}
									<Redirect to={Routes.UserBasicData.makePath(params)} />;
								</Switch>
							</UserDetailsPageContext.Provider>
						)}
					</div>
				</>
			)}
			<QueryStateIndicators queryState={userSummary} />
		</PageContainer>
	);
}
